import type { ApiClient, LegacyKuduResponseEnvelope } from '@/api/client.types';

import {
  normalizeSupportAccess,
  normalizeSupportRemoval,
  normalizeSupportUserRecord,
  normalizeSupportUsers,
  type RawSupportConsoleAccessResponse,
  type RawSupportConsoleGetUserResponse,
  type RawSupportConsoleRemoveStudentResponse,
  type RawSupportConsoleSearchUsersResponse,
  type SupportAccess,
  type SupportRemoval,
  type SupportUserRecord,
  type SupportUserSummary,
} from './supportConsoleTypes';

type MaybeEnvelope<T> = T | LegacyKuduResponseEnvelope<T>;

function unwrap<T>(payload: MaybeEnvelope<T> | null | undefined): T | null {
  if (payload && typeof payload === 'object' && 'Response' in payload) {
    return (payload as LegacyKuduResponseEnvelope<T>).Response ?? null;
  }

  return (payload as T | null | undefined) ?? null;
}

// Every support method the console calls. The service worker's tests check
// that it neither keeps the answer to any of them nor queues one for later.
export const SUPPORT_CONSOLE_METHODS = [
  'SupportConsoleAccess',
  'SupportConsoleGetUser',
  'SupportConsoleRemoveStudent',
  'SupportConsoleSearchUsers',
] as const;

// The four support calls. None of them says who is calling: the backend takes
// that from the Firebase ID token invokeKuduMethod attaches, and from nothing
// in the request. The method names start with "SupportConsole", never "Get",
// so the service worker does not keep their answers (see
// src/pwa/isStudentMutation.ts).
export function createSupportConsoleService(client: ApiClient) {
  return {
    async checkAccess(): Promise<SupportAccess> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawSupportConsoleAccessResponse>
      >('SupportConsoleAccess', {});

      return normalizeSupportAccess(unwrap(response));
    },

    async getUser(userId: string): Promise<SupportUserRecord> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawSupportConsoleGetUserResponse>,
        { user_id: string }
      >('SupportConsoleGetUser', { user_id: userId });
      const record = normalizeSupportUserRecord(unwrap(response));

      if (!record) {
        throw new Error('The server returned an incomplete user.');
      }

      return record;
    },

    async removeStudent(input: {
      courseId: string;
      userId: string;
    }): Promise<SupportRemoval> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawSupportConsoleRemoveStudentResponse>,
        { course_id: string; user_id: string }
      >('SupportConsoleRemoveStudent', {
        course_id: input.courseId,
        user_id: input.userId,
      });

      return normalizeSupportRemoval(unwrap(response));
    },

    async searchUsers(query: string): Promise<SupportUserSummary[]> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawSupportConsoleSearchUsersResponse>,
        { query: string }
      >('SupportConsoleSearchUsers', { query });

      return normalizeSupportUsers(unwrap(response));
    },
  };
}

export type SupportConsoleService = ReturnType<typeof createSupportConsoleService>;
